import type {
  ActivityItem,
  AdminSettingsStatus,
  AdminVaultRecord,
  ClientVaultContext,
  ClientRecord,
  DashboardStats,
  EventPersonalization,
  EventRecord,
  MemoryVaultUpdatePayload,
  MemoryVaultWorkspace,
  MediaItem,
  MediaKind,
  MediaStatus,
  ProposalDashboardStats,
  ProposalRecord,
  SchedulerDashboardStats,
  SchedulerRecord,
  SchedulerSettingRecord,
  ServiceAgreementRecord,
  VaultConfig,
} from "./types";

export const defaultGuestUploadInstructions =
  "Please upload your favorite photos and videos from this event. All uploads will be reviewed before they appear in the Memory Media Vault.";
export const defaultApprovalNotice =
  "Your photos and videos will be reviewed before they appear in the Memory Media Vault.";
export const defaultConfirmationMessage =
  "Thank you. Your upload has been received and will be reviewed before it appears in the Memory Media Vault.";

export function defaultEventPersonalization(clientSlug: string, eventSlug: string): EventPersonalization {
  const eventName = eventSlug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "This Event";
  return {
    clientName: clientSlug || "Client",
    clientSlug,
    eventName,
    eventSlug,
    vaultDisplayName: `${eventName} Vault`,
    eventType: "Event",
    memoryHubTitle: `${eventName} Memory Media Vault`,
    memoryHubIntroText: "Upload, view, and revisit approved memories from this special event.",
    uploadPageTitle: `${eventName} Vault`,
    uploadPageIntroText: defaultGuestUploadInstructions,
    guestUploadInstructions: defaultGuestUploadInstructions,
    galleryTitle: `${eventName} Memory Gallery`,
    galleryIntroText: "Approved photos and videos from this celebration will appear here after review.",
    emptyGalleryMessage:
      "No memories have been approved yet. Photos and videos will appear here once they are uploaded and approved.",
    primaryThemeColor: "#050505",
    secondaryThemeColor: "#FFF9EF",
    accentColor: "#D4AF37",
    uploadButtonLabel: "Upload Memories",
    approvalNotice: defaultApprovalNotice,
    confirmationMessage: defaultConfirmationMessage,
    galleryButtonLabel: "View Vault Gallery",
    memoryHubButtonLabel: "Back to Vault",
  };
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    headers: init?.body instanceof FormData ? undefined : { "Content-Type": "application/json" },
    ...init,
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getConfig(): Promise<VaultConfig> {
  return requestJson<VaultConfig>("/api/config");
}

export async function getEventPersonalization(clientSlug: string, eventSlug: string): Promise<EventPersonalization> {
  const data = await requestJson<{ event: EventPersonalization }>(`/api/event/${clientSlug}/${eventSlug}`);
  return data.event;
}

export async function getGuestUploadLink(uploadSlug: string): Promise<{
  link: {
    id: string;
    uploadSlug: string;
    status: string;
    instructions?: string;
    clientId: string;
    eventId: string;
    vaultId: string;
    vaultEventId?: string;
    clientSlug: string;
    eventSlug: string;
  };
  event: EventPersonalization;
  config?: Pick<VaultConfig, "turnstileSiteKey" | "maxPhotoSizeMb" | "maxVideoSizeMb" | "maxFilesPerUpload">;
}> {
  return requestJson(`/api/guest-upload-link/${uploadSlug}`);
}

export async function getDashboardStats(params?: {
  clientId?: string;
  eventId?: string;
  vaultId?: string;
  vaultEventId?: string;
}): Promise<DashboardStats> {
  const searchParams = new URLSearchParams();
  if (params?.clientId) searchParams.set("clientId", params.clientId);
  if (params?.eventId) searchParams.set("eventId", params.eventId);
  if (params?.vaultId) searchParams.set("vaultId", params.vaultId);
  if (params?.vaultEventId) searchParams.set("vaultEventId", params.vaultEventId);
  return requestJson<DashboardStats>(`/api/client/dashboard?${searchParams.toString()}`);
}

export async function getClientVaultContext(clientId?: string): Promise<ClientVaultContext> {
  const searchParams = new URLSearchParams();
  if (clientId) searchParams.set("clientId", clientId);
  return requestJson<ClientVaultContext>(`/api/client/context?${searchParams.toString()}`);
}

export async function getMedia(params: {
  status?: MediaStatus;
  featured?: boolean;
  type?: MediaKind | "all";
  search?: string;
  clientId?: string;
  eventId?: string;
  vaultId?: string;
  vaultEventId?: string;
}): Promise<MediaItem[]> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  if (params.featured !== undefined) searchParams.set("featured", params.featured ? "true" : "false");
  if (params.type && params.type !== "all") searchParams.set("type", params.type);
  if (params.search) searchParams.set("search", params.search);
  if (params.clientId) searchParams.set("clientId", params.clientId);
  if (params.eventId) searchParams.set("eventId", params.eventId);
  if (params.vaultId) searchParams.set("vaultId", params.vaultId);
  if (params.vaultEventId) searchParams.set("vaultEventId", params.vaultEventId);

  const data = await requestJson<{ media: MediaItem[] }>(`/api/media?${searchParams.toString()}`);
  return data.media;
}

export async function updateMediaAction(id: string, action: string, options?: { adminNotes?: string }) {
  const method = action === "delete" ? "DELETE" : "POST";
  const url = action === "delete" ? `/api/media/${id}` : `/api/media/${id}/${action}`;
  return requestJson<{ media: MediaItem }>(url, {
    method,
    body: options?.adminNotes ? JSON.stringify({ adminNotes: options.adminNotes }) : undefined,
  });
}

export async function getGallery(clientSlug: string, eventSlug: string, cursor?: string) {
  const searchParams = new URLSearchParams();
  if (cursor) searchParams.set("cursor", cursor);
  return requestJson<{ media: MediaItem[]; nextCursor?: string }>(
    `/api/gallery/${clientSlug}/${eventSlug}?${searchParams.toString()}`,
  );
}

export async function getFeatured(clientSlug: string, eventSlug: string) {
  return requestJson<{ media: MediaItem[] }>(`/api/featured/${clientSlug}/${eventSlug}`);
}

export async function getActivity(): Promise<ActivityItem[]> {
  const data = await requestJson<{ activity: ActivityItem[] }>("/api/activity-log");
  return data.activity;
}

export async function getStorageBilling() {
  return requestJson<{
    billingUrl?: string;
    fallbackMessage: string;
    supportRequestUrl: string;
    supportEmail: string;
  }>("/api/client/storage-billing");
}

export async function getMemoryVaultWorkspace(params?: { clientId?: string; eventId?: string; vaultId?: string }): Promise<MemoryVaultWorkspace> {
  const searchParams = new URLSearchParams();
  if (params?.clientId) searchParams.set("clientId", params.clientId);
  if (params?.eventId) searchParams.set("eventId", params.eventId);
  if (params?.vaultId) searchParams.set("vaultId", params.vaultId);
  const data = await requestJson<{ vault: MemoryVaultWorkspace }>(`/api/memory-vault?${searchParams.toString()}`);
  return data.vault;
}

export async function updateMemoryVaultWorkspace(payload: MemoryVaultUpdatePayload): Promise<MemoryVaultWorkspace> {
  const data = await requestJson<{ vault: MemoryVaultWorkspace }>("/api/memory-vault", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.vault;
}

export async function getAdminClients(): Promise<ClientRecord[]> {
  const data = await requestJson<{ clients: ClientRecord[] }>("/api/admin/clients");
  return data.clients;
}

export async function getAdminVaults(): Promise<AdminVaultRecord[]> {
  const data = await requestJson<{ vaults: AdminVaultRecord[] }>("/api/admin/vaults");
  return data.vaults;
}

export async function getAdminSettings(): Promise<AdminSettingsStatus> {
  return requestJson<AdminSettingsStatus>("/api/admin/settings");
}

export async function createAdminClient(payload: {
  clientName: string;
  clientEmail: string;
  clientSlug: string;
  cloudflareAccountOwnerEmail?: string;
  cloudflareBillingUrl?: string;
}) {
  return requestJson<{ client: ClientRecord }>("/api/admin/clients", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createAdminClientUser(payload: {
  clientId: string;
  email: string;
  name: string;
  role?: "client" | "viewer";
  loginNotes?: string;
}) {
  return requestJson<{ user: unknown }>("/api/admin/client-users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function resetAdminClientAccess(payload: { userId?: string; email?: string }) {
  return requestJson<{ ok: true; message: string; userId: string; email: string }>("/api/admin/reset-client-access", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createAdminVault(payload: {
  clientId: string;
  eventName: string;
  eventSlug?: string;
  vaultDisplayName?: string;
  eventType?: string;
  eventDate?: string;
  coverImage?: string;
  optionalLogo?: string;
  primaryThemeColor?: string;
  secondaryThemeColor?: string;
  accentColor?: string;
  welcomeMessage?: string;
  guestUploadInstructions?: string;
  uploadButtonText?: string;
  confirmationMessage?: string;
  approvalNotice?: string;
}) {
  return requestJson<{
    event: EventRecord;
    vault: unknown;
    vaultEvent: unknown;
    guestUploadLink: { id: string; upload_slug?: string; share_url?: string; share_url_placeholder?: string };
  }>("/api/admin/vaults", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function generateAdminGuestUploadLink(payload: {
  clientId?: string;
  eventId?: string;
  guestUploadLinkId?: string;
  regenerate?: boolean;
}) {
  return requestJson<{ link: { id: string; upload_slug?: string; share_url?: string; share_url_placeholder?: string } }>(
    "/api/admin/guest-upload-links",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export async function getProposalDashboardStats() {
  return requestJson<ProposalDashboardStats>("/api/admin/proposal-dashboard");
}

export async function getAdminProposals() {
  const data = await requestJson<{ proposals: ProposalRecord[] }>("/api/admin/proposals");
  return data.proposals;
}

export async function createAdminProposal(payload: Record<string, unknown>) {
  return requestJson<{ proposal: ProposalRecord }>("/api/admin/proposals", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminProposal(id: string, payload: Record<string, unknown>) {
  return requestJson<{ proposal: ProposalRecord }>(`/api/admin/proposals/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function convertProposalToAgreement(id: string) {
  return requestJson<{ agreement: ServiceAgreementRecord }>(`/api/admin/proposals/${id}/convert`, { method: "POST" });
}

export async function sendProposalEmail(id: string) {
  return requestJson<{ ok: true; message: string }>(`/api/admin/proposals/${id}/send`, { method: "POST" });
}

export async function getAdminAgreements() {
  const data = await requestJson<{ agreements: ServiceAgreementRecord[] }>("/api/admin/agreements");
  return data.agreements;
}

export async function createAdminAgreement(payload: Record<string, unknown>) {
  return requestJson<{ agreement: ServiceAgreementRecord }>("/api/admin/agreements", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminAgreement(id: string, payload: Record<string, unknown>) {
  return requestJson<{ agreement: ServiceAgreementRecord }>(`/api/admin/agreements/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function sendAgreementEmail(id: string) {
  return requestJson<{ ok: true; message: string }>(`/api/admin/agreements/${id}/send`, { method: "POST" });
}

export async function createAgreementPaymentLink(id: string) {
  return requestJson<{ ok: true; paymentUrl?: string; message: string }>(`/api/admin/agreements/${id}/payment-link`, {
    method: "POST",
  });
}

export async function getAdminSchedulerData() {
  return requestJson<{ settings: SchedulerSettingRecord[]; records: SchedulerRecord[]; stats: SchedulerDashboardStats }>("/api/admin/scheduler");
}

export async function updateAdminSchedulerSettings(settings: SchedulerSettingRecord[]) {
  return requestJson<{ settings: SchedulerSettingRecord[] }>("/api/admin/scheduler", {
    method: "PATCH",
    body: JSON.stringify({ settings }),
  });
}

export async function updateAdminSchedulerRecord(id: string, payload: Record<string, unknown>) {
  return requestJson<{ record: SchedulerRecord }>(`/api/admin/scheduler/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function sendAdminSchedulerLink(id: string) {
  return requestJson<{ ok: true; record: SchedulerRecord; message: string }>(`/api/admin/scheduler/${id}/send`, { method: "POST" });
}

export async function getPublicProposal(token: string) {
  return requestJson<{ proposal: ProposalRecord }>(`/api/proposals/${token}`);
}

export async function respondToProposal(
  token: string,
  payload: { action: "accept" | "decline" | "questions"; comments?: string; confirmations?: Record<string, boolean> },
) {
  return requestJson<{ proposal: ProposalRecord; agreement?: ServiceAgreementRecord; message: string }>(`/api/proposals/${token}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getPublicAgreement(token: string) {
  return requestJson<{ agreement: ServiceAgreementRecord }>(`/api/agreements/${token}`);
}

export async function signPublicAgreement(token: string, payload: Record<string, unknown>) {
  return requestJson<{ agreement: ServiceAgreementRecord; message: string }>(`/api/agreements/${token}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function login(email: string, admin = false) {
  return requestJson<{ message: string; devCode?: string }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, admin }),
  });
}

export async function verifyLogin(email: string, code: string, admin = false) {
  return requestJson<{ user: { email: string; role: string } }>("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({ email, code, admin }),
  });
}

export async function getSession() {
  return requestJson<{ user: { email: string; role: string } | null }>("/api/auth/session");
}

export async function logout() {
  return requestJson<{ ok: true }>("/api/auth/logout", { method: "POST" });
}
